import React, { useState, useEffect ,useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Typography, Spin, Carousel, Space, Divider } from 'antd';
import { 
  PrinterOutlined, 
  HeartOutlined, 
  StarOutlined, 
  EyeOutlined, 
  ClockCircleOutlined, 
  CalendarOutlined,
  TagOutlined,
  InfoCircleOutlined,
  FireOutlined,
  CloseOutlined,
  LeftOutlined,
  CrownOutlined
} from '@ant-design/icons';

const { Content } = Layout;
const { Title, Text } = Typography;

// 获取产品详情
const fetchProductDetail = async (productId: number) => {
  try {
    const response = await fetch(`/api/printed_products/${productId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch product detail:', error);
    return null;
  }
};

// 更新观看次数
const incrementViewCount = async (productId: number) => {
  try {
    const response = await fetch(`/api/printed_products/${productId}/view`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error('更新观看次数失败');
    }
    return await response.json();
  } catch (error) {
    console.error('更新观看次数失败:', error);
  }
};

// 分类数据（从 App.tsx 复制）
const categories = [
  { id: 'all', name: '全部', icon: <StarOutlined />, isSpecial: true },
  { id: 'decorative', name: '装饰品', icon: null },
  { id: 'functional', name: '功能件', icon: null },
  { id: 'toy', name: '玩具', icon: null },
  { id: 'art', name: '艺术品', icon: null },
  { id: 'tool', name: '工具', icon: null },
  { id: 'other', name: '其他', icon: <CrownOutlined />, isSpecial: true },
];

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [favorited, setFavorited] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const carouselRef = useRef<any>(null);

  // 分类颜色/名称映射
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'decorative': return '#d32f2f';
      case 'functional': return '#1890ff';
      case 'toy': return '#ff9800';
      case 'educational': return '#4caf50';
      case 'custom': return '#9c27b0';
      default: return '#795548';
    }
  };
  
  const getCategoryName = (category: string) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.name : category;
  };

  // 从 localStorage 获取初始状态
  const getInitialLikeState = (productId: number) => {
    try {
      const likedProducts = JSON.parse(localStorage.getItem('likedProducts') || '{}');
      return likedProducts[productId] || false;
    } catch {
      return false;
    }
  };

  const getInitialFavoriteState = (productId: number) => {
    try {
      const favoritedProducts = JSON.parse(localStorage.getItem('favoritedProducts') || '{}');
      return favoritedProducts[productId] || false;
    } catch {
      return false;
    }
  };

  // 更新 localStorage 中的点赞状态
  const updateLocalStorageLike = (productId: number, isLiked: boolean) => {
    try {
      const likedProducts = JSON.parse(localStorage.getItem('likedProducts') || '{}');
      likedProducts[productId] = isLiked;
      localStorage.setItem('likedProducts', JSON.stringify(likedProducts));
    } catch (error) {
      console.error('更新 localStorage 失败:', error);
    }
  };

  // 更新 localStorage 中的收藏状态
  const updateLocalStorageFavorite = (productId: number, isFavorited: boolean) => {
    try {
      const favoritedProducts = JSON.parse(localStorage.getItem('favoritedProducts') || '{}');
      favoritedProducts[productId] = isFavorited;
      localStorage.setItem('favoritedProducts', JSON.stringify(favoritedProducts));
    } catch (error) {
      console.error('更新 localStorage 失败:', error);
    }
  };

  const toggleFavoriteAPI = async (productId: number) => {
    try {
      const response = await fetch(`/api/printed_products/${productId}/favorite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('收藏操作失败');
      }
      return await response.json();
    } catch (error) {
      console.error('收藏操作失败:', error);
      throw error;
    }
  };

  const likeProduct = async (productId: number) => {
    try {
      const response = await fetch(`/api/printed_products/${productId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('点赞失败');
      }
      return await response.json();
    } catch (error) {
      console.error('点赞失败:', error);
      throw error;
    }
  };

  const handleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product) return;
    
    try {
      const newFavorited = !favorited;
      
      // 乐观更新 UI
      setFavorited(newFavorited);
      setFavoriteCount((prev: number) => newFavorited ? prev + 1 : prev - 1);
      
      // 更新 localStorage
      updateLocalStorageFavorite(product.id, newFavorited);
      
      // 调用 API
      await toggleFavoriteAPI(product.id);
    } catch (error) {
      console.error('收藏失败:', error);
      // 回滚 UI 状态
      setFavorited(favorited);
      setFavoriteCount((prev: number) => favorited ? prev : prev - 1);
      // 回滚 localStorage
      updateLocalStorageFavorite(product.id, favorited);
    }
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product) return;
    
    try {
      const newLiked = !liked;
      
      // 乐观更新 UI
      setLiked(newLiked);
      setLikeCount((prev: number) => newLiked ? prev + 1 : prev - 1);
      
      // 更新 localStorage
      updateLocalStorageLike(product.id, newLiked);
      
      // 调用 API
      await likeProduct(product.id);
    } catch (error) {
      console.error('点赞失败:', error);
      // 回滚 UI 状态
      setLiked(liked);
      setLikeCount((prev: number) => liked ? prev : prev - 1);
      // 回滚 localStorage
      updateLocalStorageLike(product.id, liked);
    }
  };


  // 处理Carousel变化
  const handleCarouselChange = (current: number) => {
    console.log(`Carousel变化: 从 ${currentImageIndex} 到 ${current}`);
    setCurrentImageIndex(current);
  };

  useEffect(() => {
    const loadProduct = async () => {
      if (!id) return;
      
    // 如果URL已经是完整URL，直接返回
      try {
        const productId = parseInt(id, 10);
        console.log(`开始加载产品详情，产品ID: ${productId}`);
        const productData = await fetchProductDetail(productId);
        console.log('产品数据加载完成:', productData);
        setProduct(productData);
        
        if (productData) {
          // 设置初始状态
          setLiked(getInitialLikeState(productData.id));
          setLikeCount(productData.like_count || 0);
          setFavorited(getInitialFavoriteState(productData.id));
          setFavoriteCount(productData.favorite_count || 0);
          
          // 调试图片信息
          if (productData.images && productData.images.length > 0) {
            console.log(`产品包含 ${productData.images.length} 张图片:`);
            productData.images.forEach((img: string, index: number) => {
              console.log(`  图片${index + 1}: ${img}`);
            });
          } else {
            console.log('产品没有图片');
          }
          
          // 更新观看次数
          await incrementViewCount(productData.id);
        }
      } catch (error) {
        console.error('Failed to load product:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadProduct();
  }, [id]);

  // 调试容器尺寸和图片状态
  useEffect(() => {
    if (product && product.images && product.images.length > 0) {
      console.log('产品详情组件渲染状态:', {
        productId: product.id,
        title: product.title,
        imageCount: product.images.length,
        currentImageIndex,
        carouselRef: carouselRef.current ? '已初始化' : '未初始化'
      });
      
      // 检查图片URL
      product.images.forEach((img: string, index: number) => {
        const imgElement = new Image();
        imgElement.onload = () => {
          console.log(`图片${index}预加载成功:`, {
            src: img,
            width: imgElement.width,
            height: imgElement.height,
            naturalWidth: imgElement.naturalWidth,
            naturalHeight: imgElement.naturalHeight
          });
        };
        imgElement.onerror = () => {
          console.error(`图片${index}预加载失败:`, img);
        };
        imgElement.src = img;
      });
    }
  }, [product, currentImageIndex]);

  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipe: true,
    draggable: true,
    arrows: false,
    adaptiveHeight: false,
    dotPosition: 'bottom' as const,
    beforeChange: (current: number, next: number) => {
      handleCarouselChange(next);
    },
    customPaging: (i: number) => (
      <div style={{
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: i === currentImageIndex ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
        margin: '0 3px',
        cursor: 'pointer',
        border: i === currentImageIndex ? 'none' : '1px solid rgba(255, 255, 255, 0.3)'
      }} />
    )
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        padding: '20px'
      }}>
        <PrinterOutlined style={{ fontSize: '64px', color: '#d9d9d9', marginBottom: '16px' }} />
        <Title level={4} style={{ color: '#999' }}>产品不存在</Title>
        <Text type="secondary">抱歉，您访问的产品不存在或已被删除</Text>
        <button
          onClick={() => navigate('/')}
          style={{
            marginTop: '20px',
            padding: '8px 16px',
            background: '#d32f2f',
            color: 'white',
            border: 'none',
            borderRadius: '20px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          返回首页
        </button>
      </div>
    );
  }

  return (
    <div style={{
      background: '#ffffff',
      minHeight: '100vh',
      position: 'relative'
    }}>
      {/* 返回按钮 */}
      <div 
        onClick={() => navigate(-1)}
        style={{
          position: 'fixed',
          top: '12px',
          left: '12px',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          cursor: 'pointer',
          backdropFilter: 'blur(4px)'
        }}
      >
        <LeftOutlined style={{ color: '#ffffff', fontSize: '20px' }} />
      </div>
      
      {/* 小红书风格布局：整个容器可滚动 */}
      <div style={{
        background: '#ffffff',
        minHeight: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        {/* 简化图片显示区域 */}
        <div style={{
          position: 'relative',
          width: '100%',
          background: '#f5f5f5',
          overflow: 'hidden',
          height: '400px' // 固定高度，简化显示
        }}>
          {/* 分类标签 */}
          <div style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            zIndex: 10,
            backdropFilter: 'blur(4px)'
          }}>
            {product.is_featured && <FireOutlined style={{ fontSize: '12px' }} />}
            {getCategoryName(product.category)}
          </div>
          
          {/* 图片数量指示器 */}
          {product.images && product.images.length > 1 && (
            <div style={{
              position: 'absolute',
              bottom: '16px',
              right: '16px',
              background: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '11px',
              zIndex: 10,
              backdropFilter: 'blur(4px)'
            }}>
              {currentImageIndex + 1} / {product.images.length}
            </div>
          )}

          {/* 简化Carousel */}
          {product.images && product.images.length > 0 ? (
            <Carousel 
              {...carouselSettings} 
              ref={carouselRef}
              style={{ 
                width: '100%',
                height: '100%'
              }}
            >
              {product.images.map((image: string, index: number) => (
                <div key={index} style={{ 
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f0f0f0'
                }}>
                  <img
                    src={image}
                    alt={`${product.title} - 图片${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                    onLoad={(e) => {
                      const img = e.target as HTMLImageElement;
                      console.log(`图片${index}加载完成:`, {
                        src: img.src,
                        naturalWidth: img.naturalWidth,
                        naturalHeight: img.naturalHeight,
                        clientWidth: img.clientWidth,
                        clientHeight: img.clientHeight
                      });
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div style="
                            width: 100%;
                            height: 100%;
                            background: linear-gradient(135deg, ${getCategoryColor(product.category)}, #ffcdd2);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                          ">
                            <PrinterOutlined style="font-size: 48px; color: rgba(255, 255, 255, 0.8)" />
                          </div>
                        `;
                      }
                    }}
                  />
                </div>
              ))}
            </Carousel>
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              background: `linear-gradient(135deg, ${getCategoryColor(product.category)}, #ffcdd2)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <PrinterOutlined style={{ fontSize: '64px', color: 'rgba(255, 255, 255, 0.8)' }} />
            </div>
          )}
        </div>

        {/* 文字内容区域 - 小红书风格 */}
        <div style={{
          padding: '20px 16px 40px 16px',
          background: '#ffffff'
        }}>
          {/* 标题和元信息 */}
          <div style={{ marginBottom: '20px' }}>
            <Title level={3} style={{ 
              margin: 0, 
              marginBottom: '16px',
              color: '#1f1f1f',
              fontSize: '22px',
              fontWeight: 600,
              lineHeight: 1.4
            }}>
              {product.title}
            </Title>
            
            {/* 交互按钮行 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              marginBottom: '20px',
              padding: '12px 0',
              borderTop: '1px solid #f0f0f0',
              borderBottom: '1px solid #f0f0f0'
            }}>
              {/* 点赞按钮 */}
              <div 
                onClick={handleLike}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  background: liked ? 'rgba(255, 107, 107, 0.1)' : 'transparent',
                  transition: 'all 0.2s'
                }}
              >
                <HeartOutlined style={{ 
                  fontSize: '24px', 
                  color: liked ? '#ff6b6b' : '#666',
                  marginBottom: '4px'
                }} />
                <Text style={{ 
                  fontSize: '14px', 
                  color: liked ? '#ff6b6b' : '#666',
                  fontWeight: 500
                }}>
                  {likeCount}
                </Text>
              </div>
              
              {/* 收藏按钮 */}
              <div 
                onClick={handleFavorite}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  background: favorited ? 'rgba(255, 215, 0, 0.1)' : 'transparent',
                  transition: 'all 0.2s'
                }}
              >
                <StarOutlined style={{ 
                  fontSize: '24px', 
                  color: favorited ? '#ffb300' : '#666',
                  marginBottom: '4px'
                }} />
                <Text style={{ 
                  fontSize: '14px', 
                  color: favorited ? '#ffb300' : '#666',
                  fontWeight: 500
                }}>
                  {favoriteCount}
                </Text>
              </div>
              
              {/* 观看按钮 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '8px 16px',
                borderRadius: '8px'
              }}>
                <EyeOutlined style={{ 
                  fontSize: '24px', 
                  color: '#1890ff',
                  marginBottom: '4px'
                }} />
                <Text style={{ 
                  fontSize: '14px', 
                  color: '#1890ff',
                  fontWeight: 500
                }}>
                  {product.view_count || 0}
                </Text>
              </div>
            </div>
            
            {/* 元信息网格 - 小红书风格 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '16px'
            }}>
              {/* 打印时间 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <ClockCircleOutlined style={{ fontSize: '14px', color: '#52c41a' }} />
                <div>
                  <div style={{ fontSize: '12px', color: '#666' }}>打印时间</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f1f1f' }}>
                    {product.print_duration ? `${Math.floor(product.print_duration / 60)}小时` : '未知'}
                  </div>
                </div>
              </div>
              
              {/* 耗材用量 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <PrinterOutlined style={{ fontSize: '14px', color: '#722ed1' }} />
                <div>
                  <div style={{ fontSize: '12px', color: '#666' }}>耗材用量</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f1f1f' }}>
                    {product.material_consumption ? `${product.material_consumption}g` : '未知'}
                  </div>
                </div>
              </div>
              
              {/* 创建时间 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <CalendarOutlined style={{ fontSize: '14px', color: '#fa8c16' }} />
                <div>
                  <div style={{ fontSize: '12px', color: '#666' }}>创建时间</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1f1f1f' }}>
                    {product.created_at ? new Date(product.created_at).toLocaleDateString() : '未知'}
                  </div>
                </div>
              </div>
              
              {/* 分类标签 */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <TagOutlined style={{ fontSize: '14px', color: getCategoryColor(product.category) }} />
                <div>
                  <div style={{ fontSize: '12px', color: '#666' }}>分类</div>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: getCategoryColor(product.category)
                  }}>
                    {getCategoryName(product.category)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 描述区域 */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              fontSize: '15px',
              lineHeight: 1.6,
              color: '#333',
              whiteSpace: 'pre-line'
            }}>
              {product.description || '暂无详细描述'}
            </div>
          </div>

          {/* 底部信息卡片 */}
          <div style={{
            padding: '16px',
            background: '#fafafa',
            borderRadius: '12px',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: '#666',
              fontSize: '13px',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <InfoCircleOutlined />
                <span>3D打印作品</span>
              </div>
              <div>
                最后更新: {product.updated_at ? new Date(product.updated_at).toLocaleDateString() : '未知'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
